import { PublicRoutes } from "@/constants/routes";
import { Button, Heading, Metric, Text } from "@/design-system";
import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import {
  CookieConsent,
  DemoFormModal,
  Footer,
  Header,
  WhatsAppFloatButton,
} from "@/pages/landingPage/components";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router";
import { FreeTrialCard } from "./components/freeTrialCard";
import { IntegrationGroup } from "./components/integrationGroup";
import { MissingIntegrationBand } from "./components/missingIntegrationBand";
import {
  API_INTEGRATIONS,
  SHEET_INTEGRATIONS,
} from "@/pages/landingPage/constants/integrations";
import {
  BREADCRUMB,
  DESCRIPTION,
  HERO_METRICS,
  INTEGRATIONS_LIST,
  OG_TITLE,
  PATH,
  SITE_URL,
  TITLE,
} from "./constants";

export const Integracoes = () => {
  useScrollDepth();
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const { hash } = useLocation();

  // O menu "Integrações" do header aponta para /integracoes#<sistema>; o
  // router não rola até a âncora sozinho.
  useEffect(() => {
    if (!hash) return;
    document
      .getElementById(decodeURIComponent(hash.slice(1)))
      ?.scrollIntoView({ block: "start" });
  }, [hash]);

  const openDemo = (location: "header" | "hero" | "free_trial") => {
    trackEvent({ event: "cta_click_specialist", location });
    setIsDemoOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={`${SITE_URL}${PATH}`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={OG_TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}${PATH}`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={OG_TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">
          {JSON.stringify(INTEGRATIONS_LIST)}
        </script>
      </Helmet>

      <div className="bg-ds-paper min-h-screen">
        <Header onSpecialistClick={() => openDemo("header")} />
        <main className="flex flex-col gap-16 pb-16 md:gap-20">
          <section
            aria-labelledby="hero-title"
            className="mx-auto flex w-full max-w-6xl flex-col items-start gap-5 px-4 pt-32 sm:px-6 md:pt-40 lg:px-8"
          >
            <Text size="small" tone="brand" weight="semibold">
              Integrações
            </Text>
            <Heading
              level={1}
              id="hero-title"
              className="md:text-ds-display max-w-3xl"
            >
              A Fidexa se conecta ao ERP que sua loja já usa
            </Heading>
            <Text size="lead" tone="muted" className="max-w-2xl">
              Por API ou por planilha, a carteira do crediário próprio entra na
              Fidexa e a{" "}
              <Link
                to={PublicRoutes.COBRANCA_WHATSAPP}
                className="text-ds-brand-700 hover:text-ds-brand-800 focus-visible:outline-ds-focus cursor-pointer underline underline-offset-4 focus-visible:outline-2"
              >
                cobrança roda no WhatsApp
              </Link>
              , do aviso antes do vencimento ao acordo de parcelamento.
            </Text>
            <Button size="lg" onClick={() => openDemo("hero")}>
              Começar teste grátis
            </Button>
            <ul className="mt-4 flex flex-wrap gap-x-10 gap-y-4">
              {HERO_METRICS.map(metric => (
                <li key={metric.label}>
                  <Metric value={metric.value} label={metric.label} />
                </li>
              ))}
            </ul>
          </section>

          <IntegrationGroup
            id="api-title"
            title="Conexão por API"
            description="A carteira sincroniza direto com o sistema da loja, sem exportar arquivo."
            integrations={API_INTEGRATIONS}
          />

          <IntegrationGroup
            id="planilha-title"
            title="Importação por planilha"
            description="Você exporta do seu sistema o arquivo com clientes, valores devidos e datas de vencimento. A importação é feita pela Fidexa, sem custo adicional."
            integrations={SHEET_INTEGRATIONS}
          />

          <FreeTrialCard onStart={() => openDemo("free_trial")} />

          <MissingIntegrationBand />
        </main>
        <Footer />
        <WhatsAppFloatButton />
        <CookieConsent />
        <DemoFormModal
          isOpen={isDemoOpen}
          onClose={() => setIsDemoOpen(false)}
          title="Comece seu teste grátis"
          description="Conte um pouco sobre sua operação. Entramos em contato no WhatsApp para começar o teste."
        />
      </div>
    </>
  );
};
